import { CdsColorBackgroundNeutralMainDisabled } from '@cegid/design-tokens';
import { Box, Button, Divider, Grid, Paper, Typography } from '@mui/material';
import { PersonAccessRightStatus } from 'Apollo';
import ContextualInfo from 'Components/ContextualInfo';
import UserStatusChip from 'Components/Shared/Chips/UserStatusChip/UserStatusChip';
import PaperFooter from 'Components/Shared/PaperFooter/PaperFooter';
import { PersonContext } from 'Context/CurrentPerson';
import useTranslate from 'Hooks/useTranslate';
import { useContext } from 'react';
import { FormattedDate } from 'react-intl';
import { Link, generatePath, useParams } from 'react-router-dom';
import { PrivateRoutes } from 'Router/Routes';
import TestIds from 'Tests/TestIds';
import Skeleton from 'Theme/components/Skeleton';
import { displayDefaultColor } from 'Tools/displayDefaultColor';
import accountLabel from 'Tools/LabelFormatter/accountLabel';
import joinURL from 'url-join';
import { useStyles } from './PersonalInformation.styles';
import UserAccountUpdate from './UserAccountUpdate';

interface Props {
  loading: boolean;
  person: IPersonWithAccount;
  accessRights: IPersonAccessRight[];
}

function PersonalInformation(props: Readonly<Props>) {
  const { accessRights, loading, person } = props;
  const { currentPersonRoles } = useContext(PersonContext);

  const classes = useStyles();
  const ts = useTranslate();
  const { accountCode } = useParams<IUriParams>();

  let accToValidate = accessRights.filter(
    acc => acc.status === PersonAccessRightStatus.ToValidate
  ).length;
  let accAccepted = accessRights.filter(
    acc => acc.status === PersonAccessRightStatus.Accepted
  ).length;

  const isDisabled = displayDefaultColor({ ...person });

  const isSuperAdmin = currentPersonRoles.canWriteAll;
  const isAccountAdmin = currentPersonRoles.isAccountAdmin;
  const canManageUserAccount = isSuperAdmin || isAccountAdmin;

  return (
    <Paper classes={{ root: classes.root }}>
      <div
        data-testid={TestIds.pages.profile.personalInformation.header}
        className={classes.header}
        style={{
          backgroundColor: isDisabled
            ? CdsColorBackgroundNeutralMainDisabled
            : person?.avatar?.color
        }}
      >
        <Grid
          container
          justifyContent="space-between"
          alignItems={'center'}
          mb={2}
          sx={{ flexDirection: { xs: 'column', md: 'row' } }}
        >
          <Typography variant="h5" color="inherit" paragraph>
            {ts('page.userProfile.section.personal.title')}
          </Typography>
        </Grid>
        <Grid
          container
          justifyContent="space-between"
          alignItems={'center'}
          sx={{ flexDirection: { xs: 'column', md: 'row' } }}
        >
          {person.account && (
            <div className={classes.accountWithHelp}>
              <Typography
                variant="body1"
                color="inherit"
                classes={{ root: classes.headerSubTitle }}
              >
                {accountLabel(person.account)}
              </Typography>
              <ContextualInfo
                color="inherit"
                content={ts('page.userProfile.accountHelp')}
                style={{ marginLeft: '8px' }}
              />
            </div>
          )}
          {canManageUserAccount && (
            <>
              <Box
                height={8}
                sx={{ display: { xs: 'block', md: 'none' } }}
                component="div"
              />
              <UserAccountUpdate person={person} accessRights={accessRights} />
            </>
          )}
        </Grid>
      </div>
      <Grid container className={classes.body}>
        <Grid item sm={4}>
          {loading ? (
            <div title="progressbar">
              <Skeleton width="5%" variant="rectangular" />
              <Skeleton width="30%" />
            </div>
          ) : (
            <>
              <Typography
                variant="h5"
                data-testid={
                  TestIds.pages.profile.personalInformation.accessRightsCount
                }
              >
                {accAccepted}
              </Typography>
              <Typography variant="body1" color="textSecondary">
                {ts('page.userProfile.personalInformations.activatedServices', {
                  value: accAccepted
                })}
              </Typography>
            </>
          )}
        </Grid>
        <Grid item sm={4}>
          {loading ? (
            <div title="progressbar">
              <Skeleton width="5%" variant="rectangular" />
              <Skeleton width="30%" />
            </div>
          ) : (
            <>
              <Typography
                variant="h5"
                data-testid={
                  TestIds.pages.profile.personalInformation
                    .pendingAccessRightsCount
                }
              >
                {accToValidate}
              </Typography>
              <Typography variant="body1" color="textSecondary">
                {ts('page.userProfile.personalInformations.pendingRequest', {
                  value: accToValidate
                })}
              </Typography>
            </>
          )}
        </Grid>
        {person.activationDate == null && (
          <Grid
            item
            sm={4}
            display={'flex'}
            alignSelf={'center'}
            justifyContent={'flex-end'}
          >
            <UserStatusChip person={person} size="medium" />
          </Grid>
        )}
        {person.activationDate !== null && (
          <Grid
            item
            sm={4}
            display={'flex'}
            flexDirection={'column'}
            justifyContent={'flex-end'}
            alignItems={'flex-end'}
          >
            <Typography
              variant="h5"
              data-testid={TestIds.pages.userProfile.personalInfo.creationDate}
            >
              <FormattedDate value={person.activationDate} />
            </Typography>
            <Typography variant="body1" color="textSecondary">
              {ts('page.userProfile.personalInformations.activatedSince')}
            </Typography>
          </Grid>
        )}
      </Grid>
      <Divider />
      <PaperFooter>
        <Button color="primary">
          <Link
            data-testid={TestIds.pages.profile.personalInformation.servicesLink}
            to={generatePath(
              joinURL(PrivateRoutes.usersProfile.path, '/services'),
              {
                accountCode: accountCode,
                immutableId: person.immutableId
              }
            )}
          >
            {ts('page.userProfile.personalInformations.seeAllServices')}
          </Link>
        </Button>
      </PaperFooter>
    </Paper>
  );
}

export default PersonalInformation;
